/**
 * Remember Us ✦ — iter210 FOUNDATION
 * ─────────────────────────────────────────────────────────────
 * A living collaborative memory wall. Vector-first. Additive.
 * Persistent. The wall slowly becomes everyone.
 *
 * Architecture rules (Craig, iter210):
 *
 *  • Vector storage primary — rasterisation is rendering only.
 *  • Strokes commit atomically on pointerup (NEVER per move).
 *  • Optimistic local render — emotional immediacy in the UI.
 *  • Wall coordinates are decoupled from screen pixels via
 *    immutable `wallWidth/wallHeight` baked into every stroke.
 *  • Performance contract: chunked offscreen accumulation.
 *    Past strokes live on a stable "ledger" canvas that is NEVER
 *    repainted; only newly-arriving strokes paint into it. The
 *    visible canvas just blits the ledger + draws the in-flight
 *    stroke. This is what survives 10k+ strokes on kiosk tablets.
 *  • iter210 ships ONE background (paper-warm neutral) — Year 6
 *    chalkboard and Secondary brick variants land in iter212.
 *  • No animation while drawing. iter211 will add the post-
 *    pointerup settle glow.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';
import useRememberUsStream from '../hooks/useRememberUsStream';
import { resolveTheme, materialVariance, strokeColor as themeStrokeColor } from '../lib/booth-os/wallThemes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter212 — palette + material now come from the resolved theme,
// which is selected by the event's boothMode. iter210 shipped a
// single hard-coded palette; iter212 promotes it to a vocabulary
// in /lib/booth-os/wallThemes.js.
const DEFAULT_WIDTHS = [2.0, 3.5, 6.0, 10.0];

// Logical wall coordinate space — every stroke is stored in this
// system, NOT the transient viewport. Renderer maps wall→screen.
const WALL_WIDTH = 1080;
const WALL_HEIGHT = 1920;
const COORDINATE_SPACE_VERSION = 1;

// Throttle ~120Hz pointer-move sampling to ~60Hz so the in-flight
// stroke stays smooth without hammering React state.
// iter198 — Throttle drops to 4ms (well under the fastest 120Hz
// touch hardware) so the limiter becomes a defensive cap rather
// than the primary rate-control. The real fidelity gain comes
// from harvesting `getCoalescedEvents()` which returns every
// sample the browser buffered between rAF paints — typically 3-8
// samples per pointermove on fast handwriting.
const MOVE_MIN_INTERVAL_MS = 4;

const RememberUsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  // iter197b — Counts ONLY the marks this participant has placed
  // on their private slate. The collective meta.total continues
  // to reflect the whole wall, but the Done gesture and the
  // "saved ✦" indicator must surface only after THIS scan has
  // actually contributed something.
  const [localStrokeCount, setLocalStrokeCount] = useState(0);
  const [boothMode, setBoothMode] = useState(null);
  // While boothMode is null (pre-meta), we show a neutral dark
  // wash — never the wedding_room theme, which would flash on
  // chalkboard/brick events.
  const theme = useMemo(() => resolveTheme(boothMode || 'wedding_room'), [boothMode]);
  const themeResolved = boothMode !== null;
  const [color, setColor] = useState(theme.defaultColor);
  const [width, setWidth] = useState(theme.defaultWidth);
  const [guestName, setGuestName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | submitting | offline

  // Two-layer renderer: the LEDGER canvas owns every committed
  // stroke (drawn once, never repainted). The LIVE canvas owns
  // only the in-flight stroke being drawn right now.
  const ledgerRef = useRef(null);
  const liveRef = useRef(null);
  const containerRef = useRef(null);
  const ledgerCtxRef = useRef(null);
  const liveCtxRef = useRef(null);

  // Current pointer-stroke state
  const drawingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const currentPointsRef = useRef([]); // [[x_wall, y_wall, t_ms, pressure]]
  const lastMoveTRef = useRef(0);
  const strokeStartTRef = useRef(0);

  // Wall→screen mapping cached so pointer→wall coordinate
  // conversion stays O(1) inside the move handler.
  const mappingRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 });

  // Submitted but not yet drawn — keeps the optimistic render
  // honest: an offline-failed stroke stays on the ledger until
  // the operator reloads.
  const seenIdsRef = useRef(new Set());
  // iter197b — Latest boot-time stroke timestamp. Used to seed
  // the SSE replay window so reconnects don't backfill all
  // historical strokes (we'd silently de-dupe them anyway, but
  // skipping the payload saves bandwidth on poor connections).
  const lastBootStrokeAtRef = useRef(null);

  // ── Renderer ──────────────────────────────────────────────
  const wallToScreen = useCallback((wallX, wallY) => {
    const m = mappingRef.current;
    return { x: m.offsetX + wallX * m.scale, y: m.offsetY + wallY * m.scale };
  }, []);

  const drawStrokeOnto = useCallback((ctx, stroke) => {
    if (!stroke?.points?.length) return;
    const material = stroke.stroke_style || theme.material;
    const variance = materialVariance(stroke, material);
    const baseColor = stroke.color || theme.defaultColor;
    const finalColor = themeStrokeColor(stroke, baseColor, material);
    ctx.save();
    ctx.globalAlpha = variance.alpha;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = finalColor;
    const w = (stroke.width || theme.defaultWidth) * variance.widthMul;
    if (stroke.points.length === 1) {
      // Single tap — render as a filled dot so taps leave a mark.
      const [x, y] = stroke.points[0];
      const s = wallToScreen(x, y);
      ctx.fillStyle = finalColor;
      ctx.beginPath();
      ctx.arc(s.x, s.y, (w * mappingRef.current.scale) / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }
    ctx.beginPath();
    const first = wallToScreen(stroke.points[0][0], stroke.points[0][1]);
    ctx.moveTo(first.x, first.y);
    // iter198 — Quadratic-midpoint smoothing.
    //
    // Straight `lineTo` between sparse samples produces the
    // "stroke → stop → stroke → stop" feel rehearsal exposed.
    // Quadratic-midpoint smoothing is the standard signature-pad
    // technique: connect each pair of samples through their
    // midpoint, using the sample itself as the control point.
    // The curve passes tangent to every real sample so:
    //   • Wobble + jitter are preserved (no fictional control pts)
    //   • No overshoot (the algorithm is bounded by sample coords)
    //   • No vector-perfect bezier calligraphy
    //   • Gaps between samples bridge smoothly — continuous ink
    //
    // Line width is set ONCE before stroking. Per-sample width
    // changes (the old loop variant) would cancel the smoothing
    // by forcing a fresh sub-path on every iteration; pressure
    // expressivity is preserved via the dust-companion pass for
    // chalk and the future per-stroke widthMul.
    const pts = stroke.points;
    let pressureSum = 0;
    let pressureN = 0;
    for (let i = 1; i < pts.length; i += 1) {
      const a = wallToScreen(pts[i - 1][0], pts[i - 1][1]);
      const b = wallToScreen(pts[i][0], pts[i][1]);
      if (i < pts.length - 1) {
        // Mid-point between current and next sample becomes the
        // anchor; current sample becomes the control point.
        const c = wallToScreen(pts[i + 1][0], pts[i + 1][1]);
        const mx = (b.x + c.x) / 2;
        const my = (b.y + c.y) / 2;
        ctx.quadraticCurveTo(b.x, b.y, mx, my);
      } else {
        // Final segment — terminate cleanly at the last sample.
        ctx.lineTo(b.x, b.y);
      }
      const p = pts[i][3];
      if (p != null) { pressureSum += p; pressureN += 1; }
      // Avoid unused-var lint on `a` while keeping the rolling
      // pair reference readable.
      void a;
    }
    const avgPressure = pressureN > 0 ? pressureSum / pressureN : 0.7;
    ctx.lineWidth = Math.max(0.5, w * mappingRef.current.scale * (0.7 + avgPressure * 0.5));
    ctx.stroke();
    // Chalk material adds a faint dust companion pass — same path,
    // wider + softer + lower opacity. The deterministic variance keeps
    // every device + replay in sync.
    if (material === 'chalk' && variance.dust > 0) {
      ctx.globalAlpha = variance.alpha * variance.dust;
      ctx.lineWidth = Math.max(1, w * mappingRef.current.scale * 1.8);
      ctx.stroke();
    }
    ctx.restore();
  }, [theme, wallToScreen]);

  // Recompute the wall→screen mapping (called on resize and when
  // the ledger is first ready). When the screen size changes we
  // must REDRAW the ledger from scratch using the cached stroke
  // list — this is the only situation where a full replay happens.
  const cachedStrokesRef = useRef([]); // committed strokes (in order)

  const repaintLedger = useCallback(() => {
    const c = ledgerRef.current;
    const ctx = ledgerCtxRef.current;
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    for (const s of cachedStrokesRef.current) drawStrokeOnto(ctx, s);
  }, [drawStrokeOnto]);

  const resizeCanvases = useCallback(() => {
    const wrap = containerRef.current;
    const ledger = ledgerRef.current;
    const live = liveRef.current;
    if (!wrap || !ledger || !live) return;
    const rect = wrap.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = rect.width;
    const cssH = rect.height;
    ledger.width = Math.max(2, Math.round(cssW * dpr));
    ledger.height = Math.max(2, Math.round(cssH * dpr));
    live.width = ledger.width;
    live.height = ledger.height;
    ledger.style.width = `${cssW}px`;
    ledger.style.height = `${cssH}px`;
    live.style.width = `${cssW}px`;
    live.style.height = `${cssH}px`;
    // Letterbox the wall coordinate space into the visible area
    // so 1080×1920 vectors look identical on every device.
    const scaleX = ledger.width / WALL_WIDTH;
    const scaleY = ledger.height / WALL_HEIGHT;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (ledger.width - WALL_WIDTH * scale) / 2;
    const offsetY = (ledger.height - WALL_HEIGHT * scale) / 2;
    mappingRef.current = { scale, offsetX, offsetY };
    repaintLedger();
  }, [repaintLedger]);

  // ── Boot: load existing wall ──────────────────────────────
  useEffect(() => {
    if (!slug) return;
    let aborted = false;
    (async () => {
      try {
        const [m, s] = await Promise.all([
          axios.get(`${API}/remember-us/events/${slug}/meta`),
          axios.get(`${API}/remember-us/events/${slug}/strokes`),
        ]);
        if (aborted) return;
        setMeta(m.data);
        const mode = m.data?.booth_mode || s.data?.booth_mode || 'wedding_room';
        setBoothMode(mode);
        // Adopt the theme's defaults the first time we resolve a mode.
        // We use `setColor((prev) => prev ?? t.defaultColor)` style so
        // the picker doesn't override what a guest has actively chosen
        // mid-session, but we DO override the placeholder pre-boot
        // color (which was bound to the wedding_room fallback).
        const t = resolveTheme(mode);
        setColor((c) => (!boothMode ? t.defaultColor : c));
        setWidth((w) => (!boothMode ? t.defaultWidth : w));
        const strokes = s.data?.strokes || [];
        // iter197b — The drawing surface is a PRIVATE contribution
        // slate, not a shared canvas. Each scan begins with an
        // empty canvas; the participant draws, hits Done, leaves.
        // The previous person's marks belong to the projector wall
        // (/live/remember-us/:slug), not this entry point.
        //
        // We still seed seenIdsRef with every prior stroke_id so
        // when SSE echoes them back (or backfills on reconnect)
        // they're de-duped silently — never painted. But the
        // local ledger starts empty.
        for (const st of strokes) {
          if (st.stroke_id) seenIdsRef.current.add(st.stroke_id);
        }
        if (strokes.length) {
          lastBootStrokeAtRef.current = strokes[strokes.length - 1].created_at || null;
        }
        cachedStrokesRef.current = [];
        setStatus('ready');
        // Let the layout settle so getBoundingClientRect is honest.
        requestAnimationFrame(() => {
          resizeCanvases();
        });
      } catch (e) {
        if (!aborted) {
          setStatus('offline');
          setErrorMsg('Couldn’t load the wall. Check your connection.');
        }
      }
    })();
    return () => { aborted = true; };
  }, [slug, resizeCanvases]);

  // ── Resize ────────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const onResize = () => resizeCanvases();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [status, resizeCanvases]);

  // ── Live stream subscription (iter211 / iter197b) ─────────
  // The personal contribution slate intentionally does NOT mirror
  // the collective wall. Other guests' strokes flow through here
  // for de-duplication (so the SSE echo of our own commit doesn't
  // paint twice), but foreign strokes are silently dropped — they
  // belong on the projector at /live/remember-us/:slug, not on the
  // participant's atomic contribution surface.
  //
  // Bumping the collective count when foreign strokes arrive keeps
  // the "X marks left behind ✦" label honest as the room writes
  // itself in real time.
  const stream = useRememberUsStream({
    apiBase: BACKEND_URL,
    slug,
    enabled: status === 'ready',
    onStroke: useCallback((stroke) => {
      if (seenIdsRef.current.has(stroke.stroke_id)) return;
      seenIdsRef.current.add(stroke.stroke_id);
      // Foreign stroke — count it on the meta tally but do NOT
      // paint it on this private slate.
      setMeta((m) => (m ? { ...m, total: (m.total || 0) + 1 } : m));
    }, []),
  });

  // Seed the stream so the SSE channel never replays the boot
  // payload back into the consumer. iter197b — the local
  // cachedStrokesRef is now always empty (private slate), so we
  // seed directly from seenIdsRef which still carries every
  // boot-time stroke_id (collected from the GET /strokes payload).
  // This keeps SSE de-dup honest even though we no longer paint
  // those strokes on this canvas.
  useEffect(() => {
    if (status !== 'ready') return;
    stream.seedSeen([...seenIdsRef.current]);
    if (lastBootStrokeAtRef.current) {
      stream.seedSince(lastBootStrokeAtRef.current);
    }
  }, [status, stream]);

  useEffect(() => {
    ledgerCtxRef.current = ledgerRef.current?.getContext('2d') || null;
    liveCtxRef.current = liveRef.current?.getContext('2d') || null;
  }, []);

  // ── Coordinate conversion: client → wall ──────────────────
  const eventToWall = (e) => {
    const c = liveRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (c.width / rect.width);
    const py = (e.clientY - rect.top) * (c.height / rect.height);
    const m = mappingRef.current;
    // Inverse-map: screen → wall.
    const x = (px - m.offsetX) / m.scale;
    const y = (py - m.offsetY) / m.scale;
    return { x, y };
  };

  // ── Pointer handlers ──────────────────────────────────────
  const onPointerDown = (e) => {
    if (status !== 'ready') return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    pointerIdRef.current = e.pointerId;
    strokeStartTRef.current = performance.now();
    lastMoveTRef.current = 0;
    const { x, y } = eventToWall(e);
    const pressure = (typeof e.pressure === 'number' && e.pressure > 0)
      ? Math.min(1, Math.max(0.05, e.pressure)) : null;
    currentPointsRef.current = [[x, y, 0, pressure]];
    // Render the initial dot immediately for tap-feedback.
    const ctx = liveCtxRef.current;
    if (ctx) {
      ctx.clearRect(0, 0, liveRef.current.width, liveRef.current.height);
      drawStrokeOnto(ctx, {
        color, width, points: currentPointsRef.current,
      });
    }
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current || e.pointerId !== pointerIdRef.current) return;
    const now = performance.now();
    if (now - lastMoveTRef.current < MOVE_MIN_INTERVAL_MS) return;
    lastMoveTRef.current = now;
    // iter198 — Harvest every buffered sample the browser
    // collected since the previous paint. On a 120Hz iPad with
    // fast cursive writing this typically returns 4-8 events;
    // capturing only the latest (the pre-iter198 behaviour) was
    // dropping the connective tissue between letters and making
    // handwriting feel like discrete touch logging.
    const events = (typeof e.getCoalescedEvents === 'function')
      ? e.getCoalescedEvents()
      : [e];
    const start = strokeStartTRef.current;
    for (const ev of events) {
      const { x, y } = eventToWall(ev);
      const t = (ev.timeStamp ? ev.timeStamp - start : now - start);
      const pressure = (typeof ev.pressure === 'number' && ev.pressure > 0)
        ? Math.min(1, Math.max(0.05, ev.pressure)) : null;
      currentPointsRef.current.push([x, y, t, pressure]);
    }
    const ctx = liveCtxRef.current;
    if (ctx) {
      ctx.clearRect(0, 0, liveRef.current.width, liveRef.current.height);
      drawStrokeOnto(ctx, {
        color, width, points: currentPointsRef.current,
      });
    }
  };

  const onPointerUp = async (e) => {
    if (!drawingRef.current || e.pointerId !== pointerIdRef.current) return;
    drawingRef.current = false;
    pointerIdRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const points = currentPointsRef.current;
    currentPointsRef.current = [];
    if (!points.length) return;

    // Round wall coordinates to 1 decimal — keeps payload compact
    // without losing perceptible fidelity.
    const compact = points.map(([x, y, t, p]) => [
      Math.round(x * 10) / 10,
      Math.round(y * 10) / 10,
      Math.round(t),
      p == null ? null : Math.round(p * 100) / 100,
    ]);

    const localId = `local_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const localStroke = {
      stroke_id: localId,
      _localFingerprint: localId,
      color, width,
      stroke_style: theme.material,
      wall_width: WALL_WIDTH, wall_height: WALL_HEIGHT,
      coordinate_space_version: COORDINATE_SPACE_VERSION,
      points: compact,
      guest_name: (guestName || '').trim() || null,
      created_at: new Date().toISOString(),
    };
    // Optimistic — commit to ledger immediately, clear live layer.
    cachedStrokesRef.current.push(localStroke);
    drawStrokeOnto(ledgerCtxRef.current, localStroke);
    // iter197b — Local count rises on every optimistic commit
    // (including offline-only ones). This gates the saved
    // indicator and the Done finishing gesture so they only
    // appear AFTER this participant has actually drawn something.
    setLocalStrokeCount((n) => n + 1);
    const liveCtx = liveCtxRef.current;
    if (liveCtx) liveCtx.clearRect(0, 0, liveRef.current.width, liveRef.current.height);

    // Atomic commit on pointerup (the architectural rule).
    setStatus('submitting');
    try {
      const r = await axios.post(`${API}/remember-us/events/${slug}/strokes`, {
        wall_width: WALL_WIDTH,
        wall_height: WALL_HEIGHT,
        coordinate_space_version: COORDINATE_SPACE_VERSION,
        stroke_style: theme.material,
        color, width,
        points: compact,
        guest_name: localStroke.guest_name,
      });
      const serverStroke = r.data?.stroke;
      if (serverStroke?.stroke_id) {
        // Replace the local optimistic stroke with the server one
        // so the de-dupe set knows about its canonical id (matters
        // for iter211 incremental fetch).
        const idx = cachedStrokesRef.current.indexOf(localStroke);
        if (idx >= 0) cachedStrokesRef.current[idx] = serverStroke;
        seenIdsRef.current.add(serverStroke.stroke_id);
      }
      setStatus('ready');
      if (meta) setMeta({ ...meta, total: (meta.total || 0) + 1 });
    } catch {
      setStatus('offline');
      setErrorMsg('Mark saved on this screen — couldn’t reach the wall server yet.');
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div
      data-testid="remember-us-page"
      style={{
        position: 'fixed', inset: 0, overflow: 'hidden',
        background: 'linear-gradient(180deg, #1a1611 0%, #0f0d0a 100%)',
        color: '#f8e8c8',
        fontFamily: 'Inter, system-ui, sans-serif',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 30 }}>
        <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="rmu-back-btn" />
      </div>

      {/* Header */}
      <div style={{
        position: 'absolute', top: 16, left: 0, right: 0, zIndex: 20,
        textAlign: 'center', pointerEvents: 'none',
      }}>
        <p style={{
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          color: 'rgba(248,232,200,0.6)', margin: 0,
          letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 11,
        }} data-testid="rmu-eyebrow">Remember Us ✦</p>
        <h1 style={{
          fontFamily: 'Georgia, "Cormorant Garamond", serif',
          fontWeight: 500, fontSize: 22, margin: '4px 0 0',
          color: '#f8e8c8',
        }} data-testid="rmu-title">Leave your mark on the night ✦</h1>
        {meta && (
          <p style={{
            margin: '4px 0 0', color: 'rgba(248,232,200,0.5)', fontSize: 11,
            fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.04em',
          }} data-testid="rmu-count">
            {meta.total} marks left behind ✦
          </p>
        )}
      </div>

      {/* Wall — letterboxed to match wall coordinate aspect */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute', inset: 0,
          paddingTop: 86, paddingBottom: 132, paddingLeft: 12, paddingRight: 12,
        }}
      >
        <div style={{
          position: 'relative', width: '100%', height: '100%',
          borderRadius: 12, overflow: 'hidden',
          background: themeResolved
            ? theme.surface.base
            : 'radial-gradient(ellipse at center, #1a1611 0%, #0b0907 80%)',
          boxShadow: themeResolved
            ? `inset 0 0 60px rgba(0,0,0,${theme.surface.vignetteFn(meta?.wall_density ?? 0)})`
            : 'inset 0 0 80px rgba(0,0,0,0.7)',
          transition: 'background 0.6s ease-in-out',
        }} data-testid="rmu-wall-surface" data-booth-mode={boothMode || 'pending'}>
          {/* Density-driven dust overlay — Craig's iter212 ageing-by-
              participation rule. Chalkboard variants register; wedding
              themes return 0 from dustOpacityFn so this stays a no-op
              on the original profiles. */}
          {themeResolved && theme.surface.dustOpacityFn(meta?.wall_density ?? 0) > 0 && (
            <div
              data-testid="rmu-dust-overlay"
              style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                background: 'radial-gradient(circle at 30% 20%, rgba(248,232,200,0.05), transparent 60%), radial-gradient(circle at 70% 80%, rgba(248,232,200,0.04), transparent 55%)',
                opacity: theme.surface.dustOpacityFn(meta?.wall_density ?? 0),
                mixBlendMode: 'screen',
              }}
            />
          )}
          <canvas
            ref={ledgerRef}
            data-testid="rmu-ledger-canvas"
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          />
          <canvas
            ref={liveRef}
            data-testid="rmu-live-canvas"
            style={{ position: 'absolute', inset: 0, touchAction: 'none', cursor: 'crosshair' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          {status === 'loading' && (
            <p style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'rgba(248,232,200,0.5)', fontFamily: 'Georgia, serif', fontStyle: 'italic',
            }} data-testid="rmu-loading">Loading the wall…</p>
          )}
        </div>
      </div>

      {/* Tool bar */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 25,
        padding: '12px 14px 18px', display: 'flex', flexDirection: 'column', gap: 10,
        background: 'linear-gradient(180deg, rgba(15,13,10,0) 0%, rgba(15,13,10,0.7) 50%, rgba(15,13,10,0.95) 100%)',
      }} data-testid="rmu-toolbar">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', opacity: themeResolved ? 1 : 0, transition: 'opacity 0.4s ease-in' }}>
          {themeResolved && theme.palette.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setColor(c.value)}
              data-testid={`rmu-color-${c.name}`}
              aria-label={c.name}
              style={{
                width: 32, height: 32, borderRadius: 999,
                background: c.value,
                border: color === c.value ? '2px solid #fff' : '2px solid rgba(255,255,255,0.15)',
                boxShadow: color === c.value ? '0 0 0 3px rgba(193,154,75,0.45)' : 'none',
                cursor: 'pointer',
              }}
            />
          ))}
          <div style={{ width: 1, height: 24, background: 'rgba(248,232,200,0.15)', margin: '0 4px' }} />
          {DEFAULT_WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWidth(w)}
              data-testid={`rmu-width-${w}`}
              aria-label={`width ${w}`}
              style={{
                width: 32, height: 32, borderRadius: 999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent',
                border: width === w ? '2px solid #c19a4b' : '2px solid rgba(255,255,255,0.15)',
                cursor: 'pointer',
              }}
            >
              <span style={{
                background: color, borderRadius: 999,
                width: Math.max(4, w * 1.6), height: Math.max(4, w * 1.6),
              }} />
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
          <input
            data-testid="rmu-guest-name"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value.slice(0, 32))}
            placeholder="your name (optional)"
            style={{
              flex: '0 1 200px', padding: '8px 12px', borderRadius: 999,
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(212,175,94,0.25)',
              color: '#f8e8c8', fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 13, textAlign: 'center',
            }}
          />
          {/* iter197 — Quiet save-status indicator. Auto-save fires
              on every pointer-up (atomic stroke commit), but
              participants don't know that. This pill makes the
              implicit save visible without ever sliding into
              SaaS-toast territory. */}
          <span
            data-testid="rmu-save-indicator"
            data-status={status}
            style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.06em',
              padding: '4px 10px', borderRadius: 999,
              border: '1px solid rgba(248,232,200,0.18)',
              color: status === 'offline'
                ? 'rgba(224,133,155,0.85)'
                : 'rgba(248,232,200,0.55)',
              background: 'rgba(0,0,0,0.32)',
              opacity: localStrokeCount > 0 || status === 'submitting' || status === 'offline' ? 1 : 0,
              transition: 'opacity 320ms ease-out',
              pointerEvents: 'none',
            }}
          >
            {status === 'submitting' ? 'saving…' : status === 'offline' ? 'saved on screen ✦' : 'saved ✦'}
          </span>
          {/* iter197 — Quiet finishing gesture. After at least one
              mark has landed, a tiny "Done ✦" button returns the
              participant to the room hub. The marks are already
              on the wall — this is closure, not commit. */}
          {localStrokeCount > 0 && (
            <button
              type="button"
              data-testid="rmu-done-btn"
              onClick={() => {
                const hub = slug ? `/access/${slug}` : '/';
                navigate(hub);
              }}
              style={{
                padding: '8px 18px', borderRadius: 999,
                fontFamily: 'Georgia, serif', fontStyle: 'italic',
                fontSize: 13, letterSpacing: '0.04em',
                color: '#f8e8c8',
                background: 'rgba(193,154,75,0.12)',
                border: '1px solid rgba(212,175,94,0.45)',
                cursor: 'pointer',
              }}
            >
              Done ✦
            </button>
          )}
        </div>
        {errorMsg && (
          <p data-testid="rmu-error" style={{ margin: 0, textAlign: 'center', color: '#e0859b', fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {errorMsg}
          </p>
        )}
      </div>
    </div>
  );
};

export default RememberUsPage;
